import React, { useEffect, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { FormTable, FormTableRow } from '../../issues/FormTable'
import { RegisteredDecimalField, RegisteredNumericField } from '../../../../components/fields/NumericField'
import { RegisteredDatePicker } from '../../../../components/fields/DatePicker'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { ActionForm, ActionFormWithId } from './ActionForm'

interface ActionDialogProps {
    convertible: ConvertibleEntity
    convertibleAction: ActionFormWithId | null
    totalAmountOutstanding: number | null
    handleSubmitConvertibleAction: (action: ActionForm) => void
    open: boolean
    handleClose: () => void
}

export function ActionDialog({
    convertible,
    convertibleAction,
    totalAmountOutstanding,
    handleSubmitConvertibleAction,
    open,
    handleClose,
}: ActionDialogProps) {
    const formMethods = useForm<ActionForm>({
        defaultValues: {
            actionType: 'conversion',
            conversionPrice: null,
            convertedShares: null,
            repayment: null,
            loanAmount: null,
            recordDate: '',
            convertibleId: '',
            id: '',
        },
    })
    const [actionType, setActionType] = useState('conversion')

    const recordDateValue = formMethods.watch('recordDate')
    const repaymentValue = formMethods.watch('repayment')
    const conversionPriceValue = formMethods.watch('conversionPrice')
    const convertedSharesValue = formMethods.watch('convertedShares')
    const loanAmountValue = formMethods.watch('loanAmount')

    useEffect(() => {
        if (!open) return

        const blankDefaults = {
            convertibleId: convertible.id,
            id: convertibleAction?.id ?? '',
            conversionPrice: null as string | null,
            convertedShares: null as number | null,
            repayment: null as string | null,
            loanAmount: null as string | null,
            recordDate: '',
        }

        if (convertibleAction) {
            formMethods.reset({
                ...blankDefaults,
                ...convertibleAction,
                convertibleId: convertible.id,
            })
            setActionType(convertibleAction.actionType)
            return
        }

        formMethods.reset({
            ...blankDefaults,
            actionType: 'conversion',
            conversionPrice: convertible.fixedConversionPrice || null,
        })
        setActionType('conversion')
    }, [convertibleAction, convertible.id, convertible.fixedConversionPrice, open])

    const handleActionType = (event: SelectChangeEvent) => {
        setActionType(event.target.value)

        formMethods.setValue('actionType', event.target.value)
        if (event.target.value === 'conversion') {
            formMethods.setValue('conversionPrice', convertible.fixedConversionPrice || null)
        } else {
            formMethods.setValue('conversionPrice', null)
        }
        formMethods.setValue('convertedShares', null)
        formMethods.setValue('repayment', null)
        formMethods.setValue('loanAmount', null)
        formMethods.setValue('recordDate', '')
    }

    const handleSubmit = () => {
        const data = formMethods.getValues()
        const newConvertibleAction = {
            ...convertibleAction,
            ...data,
            convertibleId: convertible.id,
        }
        handleSubmitConvertibleAction(newConvertibleAction)
        handleClose()
    }

    const isConversionAction = actionType === 'conversion'
    const isLoanAction = actionType === 'new_loan'

    const showRepaymentError =
        !isConversionAction &&
        !isLoanAction &&
        repaymentValue &&
        totalAmountOutstanding !== null &&
        totalAmountOutstanding < parseFloat(repaymentValue)

    const isFormValid =
        formMethods.formState.isValid &&
        recordDateValue &&
        (isLoanAction
            ? loanAmountValue
            : isConversionAction
              ? conversionPriceValue && convertedSharesValue
              : repaymentValue)

    return (
        <FormProvider {...formMethods}>
            <Dialog open={open} onClose={() => handleClose()}>
                <DialogTitle>Händelse</DialogTitle>
                <DialogContent>
                    <FormTable>
                        <FormTableRow label='Händelse'>
                            <FormControl fullWidth>
                                <InputLabel id='select-label'>Händelse</InputLabel>
                                <Select
                                    labelId='select-label'
                                    id='select'
                                    value={actionType}
                                    label='Händelse'
                                    onChange={handleActionType}
                                >
                                    <MenuItem value='conversion'>Konvertering</MenuItem>
                                    <MenuItem value='repayment'>Återbetalning</MenuItem>
                                    <MenuItem value='offset'>Kvittning</MenuItem>
                                    <MenuItem value='new_loan'>Upptagning av lån</MenuItem>
                                    <MenuItem value='write_off'>Nedskrivning</MenuItem>
                                </Select>
                            </FormControl>
                        </FormTableRow>
                        <FormTableRow label='Datum'>
                            <RegisteredDatePicker name='recordDate' label='Datum' />
                        </FormTableRow>
                        {isConversionAction ? (
                            <>
                                <FormTableRow label='Konverteringskurs'>
                                    <RegisteredDecimalField
                                        label='Konverteringskurs'
                                        name='conversionPrice'
                                        disabled={!isConversionAction}
                                    />
                                </FormTableRow>
                                <FormTableRow label='Antal aktier'>
                                    <RegisteredNumericField
                                        label='Antal'
                                        name='convertedShares'
                                        disabled={!isConversionAction}
                                    />
                                </FormTableRow>
                            </>
                        ) : isLoanAction ? (
                            <FormTableRow label='Lånebelopp'>
                                <RegisteredNumericField label='Lånebelopp' name='loanAmount' decimals={0} />
                            </FormTableRow>
                        ) : (
                            <FormTableRow label='Återbetalning'>
                                <RegisteredNumericField
                                    label='Återbetalning'
                                    name='repayment'
                                    disabled={isConversionAction || isLoanAction}
                                />
                            </FormTableRow>
                        )}
                    </FormTable>
                    {showRepaymentError ? (
                        <Typography color='error' sx={{ pt: 2 }}>
                            Återbetalningen kan inte vara större än konvertibelns belopp
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Avbryt</Button>
                    <Button disabled={!isFormValid} onClick={handleSubmit}>
                        Spara
                    </Button>
                </DialogActions>
            </Dialog>
        </FormProvider>
    )
}
